import { useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

const useAutoLogout = (timeout = 30 * 60 * 1000) => { // Default 30 minutes
  const navigate = useNavigate()
  const timeoutRef = useRef(null)
  const warningTimeoutRef = useRef(null)

  const logout = useCallback(() => {
    // Clear all user data
    localStorage.removeItem('kpca_user')
    localStorage.removeItem('kpca_token')
    localStorage.removeItem('kpca_remember_me')
    
    // Redirect to login page
    navigate('/login')
  }, [navigate])

  const showWarning = useCallback(() => {
    // Show warning 5 minutes before logout
    const confirmed = window.confirm(
      'Your session will expire in 5 minutes due to inactivity. Click OK to stay logged in.'
    )
    
    if (confirmed) {
      // Reset the timeout if user confirms
      resetTimeout()
    } else {
      // Log out immediately if user cancels
      logout()
    }
  }, [logout])

  const resetTimeout = useCallback(() => {
    // Clear existing timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current)
    }

    // Set warning timeout (5 minutes before logout)
    warningTimeoutRef.current = setTimeout(showWarning, timeout - 5 * 60 * 1000)
    
    // Set logout timeout
    timeoutRef.current = setTimeout(logout, timeout)
  }, [timeout, logout, showWarning])

  const handleActivity = useCallback(() => {
    // Reset timeout on user activity
    resetTimeout()
  }, [resetTimeout])

  useEffect(() => {
    // List of events to track for user activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown'
    ]

    // Set initial timeout
    resetTimeout()

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true)
    })

    // Cleanup function
    return () => {
      // Remove event listeners
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true)
      })
      
      // Clear timeouts
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current)
      }
    }
  }, [handleActivity, resetTimeout])

  // Return a manual logout function
  return logout
}

export default useAutoLogout 